<template>
  <SectionShell id="values" num="04" kicker="The Value Engine">
    <template #title>Five values. That's the<br />entire vocabulary of<br /><span class="hl">every</span> drawing you'll make.</template>
    <template #lede>
      <p>
        Not "shading" as a vague activity — five named steps from paper-white to
        pencil-black. Once you can hit them on demand and fade between them without a
        seam, faces stop being mysterious. Everything after this chapter is just
        deciding <i>which</i> value goes <i>where</i>.
      </p>
    </template>

    <div class="engine">
      <!-- the scale -->
      <div class="scale">
        <button
          v-for="(v, i) in values"
          :key="v.n"
          class="scale__step"
          :class="{ on: active === i }"
          :style="{ background: v.c }"
          :aria-pressed="String(active === i)"
          @mouseenter="active = i"
          @focus="active = i"
          @click="active = i"
        >
          <span class="scale__n" :style="{ color: i > 2 ? '#05070a' : '#e8ecf1' }">{{ v.n }}</span>
          <span class="scale__name" :style="{ color: i > 2 ? 'rgba(5,7,10,.72)' : 'rgba(232,236,241,.62)' }">
            {{ v.name }}
          </span>
        </button>
      </div>

      <div class="engine__grid">
        <figure class="engine__head card">
          <div class="headwrap">
            <div :style="{ filter: 'url(#poster5b)' }">
              <HeadForm :light-x="0.72" :light-y="0.24" :hair="false" />
            </div>
            <div class="pins">
              <span
                v-for="(v, i) in values"
                :key="'p' + i"
                class="pin"
                :class="[{ on: active === i }, v.side === 'l' ? 'pin--l' : 'pin--r']"
                :style="{ left: v.x + '%', top: v.y + '%' }"
              >
                <i></i>
                <em class="mono">{{ v.n }} {{ v.name }}</em>
              </span>
            </div>
          </div>
          <figcaption class="mono">the same head, crushed to five flat values</figcaption>
        </figure>

        <div class="engine__copy">
          <transition name="swap" mode="out-in">
            <div :key="active" class="def">
              <span class="def__n mono">Value {{ values[active].n }} — {{ values[active].name }}</span>
              <h3>{{ values[active].title }}</h3>
              <p>{{ values[active].body }}</p>
            </div>
          </transition>

          <div class="rulebox card">
            <h4 class="mono">The two rules of the pencil</h4>
            <ol>
              <li>
                <b>Stroke with the form, never across it.</b> On a cheek that means
                following the curve; on hair it means following the growth. Cross-hatching
                a face reads as fabric, not skin.
              </li>
              <li>
                <b>Build darkness in passes.</b> Lay a light layer, blend, look, lay
                another. Pressing hard on the first pass burnishes the paper and you can
                never lift a highlight back out of it.
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <!-- blending: good vs bad -->
    <div class="blend">
      <figure class="blend__item" v-reveal>
        <svg viewBox="0 0 320 120" class="blend__svg" role="img" aria-label="A smooth, even graduation from black to white">
          <defs>
            <linearGradient id="grad-good" x1="0" x2="1">
              <stop offset="0%" stop-color="#080a0d" />
              <stop offset="28%" stop-color="#333940" />
              <stop offset="58%" stop-color="#7b828c" />
              <stop offset="82%" stop-color="#c2c8cf" />
              <stop offset="100%" stop-color="#f6f8fa" />
            </linearGradient>
          </defs>
          <rect width="320" height="120" fill="url(#grad-good)" />
        </svg>
        <figcaption><span class="ok mono">Do</span> One unbroken graduation. Dark to white with no seam, no band, no scrub marks.</figcaption>
      </figure>

      <figure class="blend__item" v-reveal="100">
        <svg viewBox="0 0 320 120" class="blend__svg" role="img" aria-label="A scribbled, uneven attempt at shading">
          <rect width="320" height="120" fill="#20262d" />
          <g stroke="#0a0d11" stroke-width="3" stroke-linecap="round" opacity="0.85">
            <path v-for="(d, i) in scribbles" :key="i" :d="d" fill="none" />
          </g>
          <g stroke="#e6eaee" stroke-width="2.4" stroke-linecap="round" opacity="0.5">
            <path d="M212 22 L268 46" /><path d="M228 74 L286 52" /><path d="M240 100 L296 84" />
          </g>
        </svg>
        <figcaption><span class="no mono">Don't</span> Loose scribble. The strokes cross each other, so no stump on earth can pull them into a tone.</figcaption>
      </figure>
    </div>

    <svg class="defs" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <filter id="poster5b" color-interpolation-filters="sRGB">
        <feColorMatrix type="saturate" values="0" />
        <feComponentTransfer>
          <feFuncR type="discrete" tableValues="0.04 0.23 0.46 0.71 0.96" />
          <feFuncG type="discrete" tableValues="0.04 0.23 0.46 0.71 0.96" />
          <feFuncB type="discrete" tableValues="0.04 0.23 0.46 0.71 0.96" />
        </feComponentTransfer>
      </filter>
    </svg>
  </SectionShell>
</template>

<script setup>
import { ref } from 'vue'
import SectionShell from '../ui/SectionShell.vue'
import HeadForm from '../svg/HeadForm.vue'

const active = ref(2)

const values = [
  {
    n: '#1',
    name: 'black',
    c: '#0a0c10',
    x: 50,
    y: 76,
    side: 'r',
    title: 'The cast shadow — the darkest thing on the page.',
    body: 'Where light is physically blocked: under the jaw, inside a nostril, in the gap between the lips, in the shadow the hair throws on the forehead. It has a crisp edge nearest the object that casts it, softening as it travels away. Get one true black on the paper and every other value suddenly reads correctly.'
  },
  {
    n: '#2',
    name: 'dark gray',
    c: '#3a4048',
    x: 26,
    y: 42,
    side: 'r',
    title: 'The form shadow — the side the light never reaches.',
    body: 'It sits opposite the light source and it curves around the form. This is the value that does the actual work of making a flat oval look like a head. Note where it stops: it must not run all the way to the outer edge, because something has to be left over for value #4.'
  },
  {
    n: '#3',
    name: 'medium gray',
    c: '#767d87',
    x: 39,
    y: 55,
    side: 'r',
    title: 'The halftone — where light turns away.',
    body: 'Neither lit nor shadowed: the long transitional band across a cheek, the plane of the temple, the underside of a lower lip. Most of a face is halftone, and most of the blending you do is nudging halftone smoothly toward its neighbours on both sides.'
  },
  {
    n: '#4',
    name: 'light gray',
    c: '#b6bcc4',
    x: 23,
    y: 57,
    side: 'r',
    title: 'Reflected light — the hardest one to see, the one that sells it.',
    body: 'Light bouncing off the collar, the wall, the table, striking the shadow side from behind and lifting a thin rim along the edge. It separates the form shadow from the cast shadow. Miss it and heads look like flat cut-outs; overdo it and they glow like plastic. It is never white — it is #4.'
  },
  {
    n: '#5',
    name: 'white',
    c: '#f4f6f8',
    x: 60,
    y: 27,
    side: 'l',
    title: 'Full light — bare paper, and you only get so much of it.',
    body: 'The forehead plane facing the lamp, the tip of the nose, the wet shine on a lower lip, the single catchlight in each eye. Do not shade it and do not erase it back — protect it from the start. White paper you never touched always looks brighter than white paper you cleaned up.'
  }
]

/* a deliberately awful scribble, drawn once */
const scribbles = [
  'M18 20 L74 44 M28 40 L84 18 M22 62 L78 86 M32 92 L88 66',
  'M92 16 L142 52 M96 54 L150 24 M88 82 L146 100 M104 104 L152 74',
  'M156 26 L198 60 M160 66 L206 34 M154 92 L200 106'
]
</script>

<style scoped>
.scale {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1px;
  border: 1px solid var(--edge-soft);
  margin-bottom: 36px;
}
.scale__step {
  border: 0;
  border-radius: 0;
  padding: 22px 14px 18px;
  text-align: left;
  cursor: pointer;
  position: relative;
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  transform-origin: bottom;
}
.scale__step.on { transform: scaleY(1.14); z-index: 2; }
.scale__step:hover { transform: scaleY(1.14); z-index: 2; }
.scale__n { display: block; font-family: var(--font-mono); font-size: 13px; letter-spacing: 0.08em; }
.scale__name {
  display: block;
  font-family: var(--font-mono);
  font-size: 8.5px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-top: 5px;
}

.engine__grid {
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: clamp(24px, 3.5vw, 56px);
  align-items: start;
}

.engine__head { position: relative; margin: 0; overflow: hidden; background: #080b10; }
.headwrap :deep(.headform) { display: block; width: 100%; }
.engine__head figcaption {
  border-top: 1px solid var(--edge-soft);
  padding: 11px 14px;
  font-family: var(--font-mono);
  font-size: 9px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-faint);
}

.headwrap { position: relative; }
.pins { position: absolute; inset: 0; pointer-events: none; }
.pin { position: absolute; width: 0; height: 0; display: block; }
.pin i {
  position: absolute;
  left: -5px;
  top: -5px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(4, 6, 10, 0.55);
  transition: all 0.3s;
}
.pin em {
  position: absolute;
  left: 15px;
  top: -10px;
  font-style: normal;
  font-size: 8.5px;
  letter-spacing: 0.14em;
  white-space: nowrap;
  color: var(--text-faint);
  background: rgba(4, 6, 10, 0.82);
  padding: 3px 7px;
  border-radius: 2px;
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.3s;
}
.pin--l em { left: auto; right: 15px; transform: translateX(4px); }
.pin.on i { border-color: var(--light); background: var(--light); box-shadow: 0 0 0 5px var(--light-dim); }
.pin.on em { opacity: 1; transform: none; color: var(--light); }
.pin--l { flex-direction: row-reverse; }

.def { min-height: 190px; }
.def__n { color: var(--signal); font-size: 10px; display: block; margin-bottom: 12px; }
.def h3 {
  font-size: clamp(20px, 1.6vw + 13px, 30px);
  font-weight: 400;
  letter-spacing: -0.02em;
  margin-bottom: 14px;
}
.def p { color: var(--text-dim); }

.swap-enter-active, .swap-leave-active { transition: opacity 0.28s, transform 0.28s; }
.swap-enter-from { opacity: 0; transform: translateY(8px); }
.swap-leave-to { opacity: 0; transform: translateY(-8px); }

.rulebox { margin-top: 26px; padding: 24px 26px; }
.rulebox h4 { color: var(--light); font-size: 10px; font-weight: 400; margin-bottom: 16px; }
.rulebox ol { margin: 0; padding-left: 18px; color: var(--text-dim); font-size: 14px; }
.rulebox li { margin-bottom: 12px; line-height: 1.62; }
.rulebox li:last-child { margin-bottom: 0; }

.blend {
  margin-top: clamp(50px, 7vh, 88px);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 26px;
}
.blend__item { margin: 0; }
.blend__svg {
  display: block;
  width: 100%;
  height: auto;
  border: 1px solid var(--edge);
  border-radius: var(--radius);
}
.blend figcaption { margin-top: 14px; color: var(--text-dim); font-size: 13.5px; line-height: 1.6; }
.ok, .no {
  display: inline-block;
  font-size: 9px;
  padding: 3px 8px;
  border-radius: 2px;
  margin-right: 10px;
  color: var(--ink-900);
}
.ok { background: var(--signal); }
.no { background: #ff7b72; }

.defs { position: absolute; width: 0; height: 0; }

@media (max-width: 980px) {
  .engine__grid { grid-template-columns: 1fr; }
  .scale__name { display: none; }
}
</style>
